/**
 * The UI state machine (UX-SPEC §4.1, C-UI-STATES): one `phase` signal and the data each phase shows. Every view renders
 * from these signals; `controller.ts` moves between phases.
 */
import { signal } from '@preact/signals';
import type { IntakeInfo, RecentItem, StageKey, StoredJob } from '../shared/protocol.ts';
import type { ViewModel } from '../shared/viewmodel.ts';
import type { LightFormat } from './sniff.ts';

export type Phase =
	| 'empty'
	| 'reading'
	| 'fetching'
	| 'parsing'
	| 'intake-checked'
	| 'over-limit'
	| 'analysing'
	| 'rendering'
	| 'ready'
	| 'cancelled'
	| 'error'
	| 'crashed'
	| 'restoring'
	| 'interrupted';

export type IntakeTab = 'document' | 'paste' | 'url';

export interface FileInput {
	name: string;
	size: number;
	bytes: Uint8Array;
	format: LightFormat;
}

/** What the error panel shows and which action it offers (UX-SPEC §4.5). */
export interface AppError {
	code: string;
	message: string;
	action: 'choose-file' | 'edit-url' | 'retry' | 'run-again' | 'download';
	/** `rate_limited`: seconds until the button is enabled again, counted from `at`. */
	retryAfter?: number;
	at: number;
	/** Shown under the message (the diagnostics id of an internal failure). */
	detail?: string;
}

export const phase = signal<Phase>('empty');
export const tab = signal<IntakeTab>('document');
export const file = signal<FileInput | null>(null);
export const pasteText = signal('');
export const pasteIsSample = signal(false);
export const pasteWords = signal<number | null>(null);
export const urlText = signal('');

export const progress = signal<{
	stage: StageKey;
	pct: number;
	detail?: { page: number; pages: number };
}>({ stage: 'read', pct: 0 });
export const runStartedAt = signal(0);
export const sourceName = signal('');
export const fetchHost = signal('');
export const intakeInfo = signal<IntakeInfo | null>(null);
export const error = signal<AppError | null>(null);
export const result = signal<ViewModel | null>(null);

/** The result the page's own boot restored (a reload: the same session continuing), not one opened by an upload, a paste or Recent (UD-28). */
let bootRestored: ViewModel | null = null;
export const markBootRestored = (vm: ViewModel): void => {
	bootRestored = vm;
};
export const isBootRestored = (vm: ViewModel): boolean => vm === bootRestored;
export const recent = signal<RecentItem[]>([]);
export const storedJob = signal<StoredJob | null>(null);
/** Notices that sit above the results or the intake: a restore, a storage failure. */
export const notices = signal<string[]>([]);
/** Milliseconds from Run to the first overview paint (Diagnostics, T-PERF-10). */
export const wallMs = signal<number | null>(null);
/** Sequence of phases entered, for tests (`data-phases` on the root). */
export const phaseLog = signal<string[]>([]);

export function setPhase(p: Phase): void {
	phase.value = p;
	phaseLog.value = [...phaseLog.value.slice(-40), p];
}

export function addNotice(text: string): void {
	if (!notices.value.includes(text)) notices.value = [...notices.value, text];
}

export function clearNotices(): void {
	notices.value = [];
}

/** Phases that show results. */
export const showsResults = (p: Phase): boolean => p === 'ready';
